import { Component, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';

interface Card {
  id: number;
  foodType: string;
  emoji: string;
  color: string;
  isFlipped: boolean;
  isMatched: boolean;
  pairId: number;
}

interface Player {
  id: number;
  name: string;
  score: number;
  matches: number;
  isActive: boolean;
}

enum GameMode {
  SINGLE = 'single',
  DOUBLE = 'double'
}

enum Difficulty {
  EASY = 8,    // 4 pairs
  MEDIUM = 12, // 6 pairs
  HARD = 16    // 8 pairs
}

@Component({
  selector: 'app-memory-game',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="game-modal" [class.show]="showGame()">
      <div class="game-container">
        <div class="game-header">
          <h3>🧠 Juego de Parejas</h3>
          <button class="close-btn" (click)="closeGame()">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Game Setup -->
        @if (gamePhase() === 'setup') {
          <div class="setup-section">
            <div class="setup-group">
              <h4>Modo de Juego</h4>
              <div class="mode-buttons">
                <button
                  class="mode-btn"
                  [class.active]="gameMode() === GameMode.SINGLE"
                  (click)="setGameMode('single')">
                  👤 Un Jugador
                </button>
                <button
                  class="mode-btn"
                  [class.active]="gameMode() === GameMode.DOUBLE"
                  (click)="setGameMode('double')">
                  👥 Dos Jugadores
                </button>
              </div>
            </div>

            <div class="setup-group">
              <h4>Dificultad</h4>
              <div class="difficulty-buttons">
                <button
                  class="difficulty-btn"
                  [class.active]="difficulty() === 8"
                  (click)="difficulty.set(8)">
                  😊 Fácil (4 parejas)
                </button>
                <button
                  class="difficulty-btn"
                  [class.active]="difficulty() === 12"
                  (click)="difficulty.set(12)">
                  😐 Medio (6 parejas)
                </button>
                <button
                  class="difficulty-btn"
                  [class.active]="difficulty() === 16"
                  (click)="difficulty.set(16)">
                  😤 Difícil (8 parejas)
                </button>
              </div>
            </div>

            <button class="start-game-btn" (click)="startGame()">
              🎮 Empezar Juego
            </button>
          </div>
        }

        <!-- Game Board -->
        @if (gamePhase() === 'playing') {
          <div class="game-info">
            <div class="timer">
              ⏱️ {{ formatTime(gameTime()) }}
            </div>

            @if (gameMode() === 'double') {
              <div class="players-info">
                @for (player of players(); track player.id) {
                  <div class="player-info" [class.active]="player.isActive">
                    <div class="player-name">{{ player.name }}</div>
                    <div class="player-score">{{ player.matches }} parejas</div>
                  </div>
                }
              </div>
            } @else {
              <div class="score-info">
                <div class="matches">{{ foundPairs() }}/{{ totalPairs() }} parejas</div>
                <div class="moves">{{ moves() }} movimientos</div>
              </div>
            }
          </div>

          <div class="game-board" [class]="'grid-' + gridSize()">
            @for (card of cards(); track card.id) {
              <div
                class="card"
                [class.flipped]="card.isFlipped || card.isMatched"
                [class.matched]="card.isMatched"
                [class.disabled]="isCardDisabled(card)"
                (click)="flipCard(card)">
                <div class="card-inner">
                  <div class="card-front">
                    <div class="card-back-design">🍽️</div>
                  </div>
                  <div class="card-back" [style.background-color]="card.color">
                    <div class="card-emoji">{{ card.emoji }}</div>
                    <div class="card-name">{{ card.foodType }}</div>
                  </div>
                </div>
              </div>
            }
          </div>

          <div class="game-controls">
            <button class="control-btn" (click)="pauseGame()" [disabled]="gameComplete()">
              <i class="fas fa-pause"></i> Pausar
            </button>
            <button class="control-btn" (click)="resetGame()">
              <i class="fas fa-redo"></i> Reiniciar
            </button>
          </div>
        }

        <!-- Game Complete -->
        @if (gamePhase() === 'complete') {
          <div class="game-complete">
            <h3>🎉 ¡Juego Completado!</h3>

            @if (gameMode() === 'single') {
              <div class="final-stats">
                <div class="stat">
                  <span class="stat-label">Tiempo:</span>
                  <span class="stat-value">{{ formatTime(finalTime()) }}</span>
                </div>
                <div class="stat">
                  <span class="stat-label">Movimientos:</span>
                  <span class="stat-value">{{ moves() }}</span>
                </div>
                <div class="stat">
                  <span class="stat-label">Puntuación:</span>
                  <span class="stat-value score">{{ finalScore() }}</span>
                </div>
              </div>
            } @else {
              <div class="winner-section">
                @if (winner()) {
                  <div class="winner">
                    🏆 ¡{{ winner()!.name }} ha ganado!
                  </div>
                  <div class="winner-score">
                    {{ winner()!.matches }} parejas encontradas
                  </div>
                } @else {
                  <div class="tie">
                    🤝 ¡Empate!
                  </div>
                }

                <div class="final-players">
                  @for (player of players(); track player.id) {
                    <div class="final-player">
                      {{ player.name }}: {{ player.matches }} parejas
                    </div>
                  }
                </div>
              </div>
            }

            <div class="complete-actions">
              <button class="action-btn" (click)="playAgain()">
                🔄 Jugar de Nuevo
              </button>
              <button class="action-btn" (click)="closeGame()">
                🏠 Volver al Menú
              </button>
            </div>
          </div>
        }
      </div>
    </div>
  `,
  styleUrl: './memory-game.component.scss'
})
export class MemoryGameComponent {
  // Expose enums for template use
  readonly GameMode = GameMode;
  readonly Difficulty = Difficulty;

  // Game state
  showGame = signal(false);
  gamePhase = signal<'setup' | 'playing' | 'paused' | 'complete'>('setup');
  gameMode = signal<GameMode>(GameMode.SINGLE);
  difficulty = signal<Difficulty>(Difficulty.EASY);

  // Game data
  cards = signal<Card[]>([]);
  flippedCards = signal<Card[]>([]);
  players = signal<Player[]>([]);
  currentPlayerIndex = signal(0);
  moves = signal(0);
  gameTime = signal(0);
  finalTime = signal(0);
  finalScore = signal(0);

  // Game timer
  private gameTimer?: number;

  // Food types for cards
  private readonly FOOD_TYPES = [
    { type: 'Pizza', emoji: '🍕', color: '#FF6B35' },
    { type: 'Hamburguesa', emoji: '🍔', color: '#8B4513' },
    { type: 'Taco', emoji: '🌮', color: '#DAA520' },
    { type: 'Sushi', emoji: '🍣', color: '#FF69B4' },
    { type: 'Donut', emoji: '🍩', color: '#FFB6C1' },
    { type: 'Helado', emoji: '🍦', color: '#87CEEB' },
    { type: 'Pastel', emoji: '🎂', color: '#DDA0DD' },
    { type: 'Frutas', emoji: '🍓', color: '#FF1493' },
    { type: 'Café', emoji: '☕', color: '#8B4513' },
    { type: 'Croissant', emoji: '🥐', color: '#DEB887' },
    { type: 'Aguacate', emoji: '🥑', color: '#9ACD32' },
    { type: 'Banana', emoji: '🍌', color: '#22C55E' }
  ];

  // Computed properties
  gridSize = computed(() => {
    const total = this.difficulty();
    if (total === 8) return '4x2';
    if (total === 12) return '4x3';
    return '4x4';
  });

  totalPairs = computed(() => this.difficulty() / 2);
  foundPairs = computed(() =>
    this.cards().filter(card => card.isMatched).length / 2
  );

  gameComplete = computed(() =>
    this.foundPairs() === this.totalPairs()
  );

  currentPlayer = computed(() =>
    this.players()[this.currentPlayerIndex()]
  );

  winner = computed(() => {
    if (this.gameMode() !== GameMode.DOUBLE) return null;

    const players = this.players();
    const maxMatches = Math.max(...players.map(p => p.matches));
    const winners = players.filter(p => p.matches === maxMatches);

    return winners.length === 1 ? winners[0] : null;
  });

  constructor() {
    // Effect to check game completion
    effect(() => {
      if (this.gameComplete() && this.gamePhase() === 'playing') {
        this.completeGame();
      }
    });
  }

  // Public methods
  openGame() {
    this.showGame.set(true);
    this.resetToSetup();
  }

  closeGame() {
    this.showGame.set(false);
    this.stopTimer();
    this.resetToSetup();
  }

  startGame() {
    this.initializeGame();
    this.gamePhase.set('playing');
    this.startTimer();
  }

  pauseGame() {
    this.gamePhase.set('paused');
    this.stopTimer();
  }

  resumeGame() {
    this.gamePhase.set('playing');
    this.startTimer();
  }

  resetGame() {
    this.stopTimer();
    this.resetToSetup();
  }

  playAgain() {
    this.resetToSetup();
  }

  flipCard(card: Card) {
    if (this.isCardDisabled(card) || this.gamePhase() !== 'playing') {
      return;
    }

    const flipped = this.flippedCards();

    if (flipped.length < 2) {
      card.isFlipped = true;
      this.flippedCards.set([...flipped, card]);

      if (this.flippedCards().length === 2) {
        this.moves.set(this.moves() + 1);
        this.checkForMatch();
      }
    }
  }

  isCardDisabled(card: Card): boolean {
    return card.isMatched || card.isFlipped || this.flippedCards().length >= 2;
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  setGameMode(mode: 'single' | 'double') {
    this.gameMode.set(mode === 'single' ? GameMode.SINGLE : GameMode.DOUBLE);
  }

  // Private methods
  private resetToSetup() {
    this.gamePhase.set('setup');
    this.cards.set([]);
    this.flippedCards.set([]);
    this.moves.set(0);
    this.gameTime.set(0);
    this.finalTime.set(0);
    this.finalScore.set(0);
    this.currentPlayerIndex.set(0);
    this.stopTimer();
  }

  private initializeGame() {
    this.setupPlayers();
    this.setupCards();
    this.moves.set(0);
    this.gameTime.set(0);
  }

  private setupPlayers() {
    if (this.gameMode() === GameMode.SINGLE) {
      this.players.set([]);
    } else {
      this.players.set([
        { id: 1, name: 'Jugador 1', score: 0, matches: 0, isActive: true },
        { id: 2, name: 'Jugador 2', score: 0, matches: 0, isActive: false }
      ]);
    }
  }

  private setupCards() {
    const numPairs = this.totalPairs();
    const selectedFoods = this.FOOD_TYPES.slice(0, numPairs);
    const cardPairs: Card[] = [];

    selectedFoods.forEach((food, index) => {
      // Create two cards for each pair
      for (let i = 0; i < 2; i++) {
        cardPairs.push({
          id: index * 2 + i,
          foodType: food.type,
          emoji: food.emoji,
          color: food.color,
          isFlipped: false,
          isMatched: false,
          pairId: index
        });
      }
    });

    // Shuffle cards
    this.cards.set(this.shuffleArray(cardPairs));
  }

  private shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  private checkForMatch() {
    const flipped = this.flippedCards();
    const [card1, card2] = flipped;

    setTimeout(() => {
      if (card1.pairId === card2.pairId) {
        // Match found!
        card1.isMatched = true;
        card2.isMatched = true;

        if (this.gameMode() === GameMode.DOUBLE) {
          this.handleDoublePlayerMatch();
        }
      } else {
        // No match
        card1.isFlipped = false;
        card2.isFlipped = false;

        if (this.gameMode() === GameMode.DOUBLE) {
          this.switchPlayer();
        }
      }

      this.flippedCards.set([]);
      this.cards.set([...this.cards()]);
    }, 1000);
  }

  private handleDoublePlayerMatch() {
    const current = this.currentPlayer();
    if (current) {
      current.matches++;
      this.players.set([...this.players()]);
      // Player continues on match
    }
  }

  private switchPlayer() {
    const players = this.players();
    players.forEach(p => p.isActive = false);

    this.currentPlayerIndex.set(
      (this.currentPlayerIndex() + 1) % players.length
    );

    players[this.currentPlayerIndex()].isActive = true;
    this.players.set([...players]);
  }

  private startTimer() {
    this.gameTimer = window.setInterval(() => {
      this.gameTime.set(this.gameTime() + 1);
    }, 1000);
  }

  private stopTimer() {
    if (this.gameTimer) {
      clearInterval(this.gameTimer);
      this.gameTimer = undefined;
    }
  }

  private completeGame() {
    this.stopTimer();
    this.finalTime.set(this.gameTime());

    if (this.gameMode() === GameMode.SINGLE) {
      this.calculateSinglePlayerScore();
    }

    this.gamePhase.set('complete');
  }

  private calculateSinglePlayerScore() {
    const baseScore = 1000;
    const timeBonus = Math.max(0, 300 - this.finalTime()); // Bonus for speed
    const movesPenalty = Math.max(0, this.moves() - this.totalPairs() * 2) * 5; // Penalty for extra moves
    const difficultyMultiplier = this.difficulty() / 8; // Harder = more points

    const score = Math.round(
      (baseScore + timeBonus - movesPenalty) * difficultyMultiplier
    );

    this.finalScore.set(Math.max(0, score));
  }
}